import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@agms/ui'
import api from '../../lib/api'
import type { DeploymentTask } from '../../lib/api'
import { apiErrorMessage } from '../../hooks/useErrorHandler'
import {
  GATEWAY_SERVICES,
  deploymentStatusClass,
  gatewayStatusLabel,
  truncate,
} from '../../pages/merchants/merchantFormatters'
import type { GatewayServiceDefinition } from '../../pages/merchants/merchantFormatters'
import { useMerchantNmiStatus, type MerchantNmiStatus } from '../../pages/merchants/useMerchantData'

type NmiMutableStatus = 'active' | 'closed' | 'restricted'

const NMI_MUTABLE_STATUSES: Array<{ value: NmiMutableStatus; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'closed', label: 'Closed' },
  { value: 'restricted', label: 'Restricted' },
]

interface GatewayServicesPanelProps {
  merchantId: string | null
  deploymentTasks?: { data: DeploymentTask[] }
  gatewayError: string | null
  retryPending: boolean
  cancelPending: boolean
  canManageGatewayServices: boolean
  canRetryDeploymentTasks: boolean
  canManageNmiStatus: boolean
  onAddService: (service: GatewayServiceDefinition) => void
  onRetry: (taskId: string) => void
  onCancel: (task: DeploymentTask) => void
}

interface MerchantDejavooTerminalStatus {
  tpn: string
  status: 'loaded' | 'incomplete' | 'no_download' | string
  last_download: string | null
  last_download_end: string | null
  last_loaded: string | null
  serial_number: string | null
  am_version_to: string | null
  network: string | null
  download_count: number
  http_status?: number
}

type DejavooActiveSelection = 'active' | 'inactive'

export function GatewayServicesPanel({
  merchantId,
  deploymentTasks,
  gatewayError,
  retryPending,
  cancelPending,
  canManageGatewayServices,
  canRetryDeploymentTasks,
  canManageNmiStatus,
  onAddService,
  onRetry,
  onCancel,
}: GatewayServicesPanelProps) {
  const queryClient = useQueryClient()
  const nmiCompleted = deploymentTasks?.data.some((item) => (
    item.adapter_type === 'service_nmi'
      && item.status === 'completed'
      && !!item.vendor_ref_id
  )) ?? false
  const nmiStatusQuery = useMerchantNmiStatus(merchantId, nmiCompleted)
  const [selectedNmiStatus, setSelectedNmiStatus] = useState<NmiMutableStatus>('active')
  const [nmiStatusError, setNmiStatusError] = useState<string | null>(null)
  const nmiStatus = nmiStatusQuery.data?.status ?? null
  const [dejavooStatuses, setDejavooStatuses] = useState<Record<string, {
    data?: MerchantDejavooTerminalStatus
    error?: string
    loading?: boolean
  }>>({})
  const [dejavooActiveSelections, setDejavooActiveSelections] = useState<Record<string, DejavooActiveSelection>>({})
  const [dejavooActivePendingTpn, setDejavooActivePendingTpn] = useState<string | null>(null)
  const [dejavooActiveMessages, setDejavooActiveMessages] = useState<Record<string, string | null>>({})

  useEffect(() => {
    if (isMutableNmiStatus(nmiStatus)) {
      setSelectedNmiStatus(nmiStatus)
    }
  }, [nmiStatus])

  const updateNmiStatusMutation = useMutation({
    mutationFn: async (status: NmiMutableStatus) => {
      const resp = await api.post<MerchantNmiStatus>(`/merchants/${merchantId}/nmi/status`, { status })
      return resp.data
    },
    onSuccess: (data) => {
      setNmiStatusError(null)
      if (isMutableNmiStatus(data.status)) {
        setSelectedNmiStatus(data.status)
      }
      queryClient.invalidateQueries({ queryKey: ['merchant-nmi-status', merchantId] })
    },
    onError: (error) => {
      setNmiStatusError(apiErrorMessage(error, 'Unable to update NMI gateway status.'))
    },
  })

  const submitNmiStatus = () => {
    if (!merchantId || updateNmiStatusMutation.isPending) return

    if (
      (selectedNmiStatus === 'closed' || selectedNmiStatus === 'restricted')
      && !window.confirm(`Set NMI gateway status to ${statusLabel(selectedNmiStatus)}? This affects a live merchant.`)
    ) {
      return
    }

    updateNmiStatusMutation.mutate(selectedNmiStatus)
  }

  const checkDejavooStatus = async (tpn: string) => {
    if (!merchantId || !tpn) return

    setDejavooStatuses((current) => ({
      ...current,
      [tpn]: { ...current[tpn], loading: true, error: undefined },
    }))

    try {
      const resp = await api.get<MerchantDejavooTerminalStatus>(`/merchants/${merchantId}/dejavoo/terminals/${encodeURIComponent(tpn)}/status`)
      setDejavooStatuses((current) => ({
        ...current,
        [tpn]: { data: resp.data, loading: false },
      }))
    } catch (error) {
      setDejavooStatuses((current) => ({
        ...current,
        [tpn]: {
          ...current[tpn],
          loading: false,
          error: apiErrorMessage(error, 'Unable to check Dejavoo terminal status.'),
        },
      }))
    }
  }

  const submitDejavooActiveStatus = async (tpn: string) => {
    if (!merchantId || !tpn || dejavooActivePendingTpn) return

    const selection = dejavooActiveSelections[tpn] ?? 'active'
    const active = selection === 'active'
    if (!active && !window.confirm(`Set Dejavoo terminal ${tpn} to inactive? This affects a live terminal.`)) {
      return
    }

    setDejavooActivePendingTpn(tpn)
    setDejavooActiveMessages((current) => ({ ...current, [tpn]: null }))

    try {
      await api.post(`/merchants/${merchantId}/dejavoo/terminals/${encodeURIComponent(tpn)}/active`, { active })
      setDejavooActiveMessages((current) => ({
        ...current,
        [tpn]: `Terminal set ${active ? 'active' : 'inactive'}.`,
      }))
    } catch (error) {
      setDejavooActiveMessages((current) => ({
        ...current,
        [tpn]: apiErrorMessage(error, 'Unable to update Dejavoo terminal active status.'),
      }))
    } finally {
      setDejavooActivePendingTpn(null)
    }
  }

  return (
    <Card>
      <CardHeader><CardTitle>Gateway Services</CardTitle></CardHeader>
      <CardContent>
        {gatewayError && (
          <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            {gatewayError}
          </div>
        )}

        <div className="divide-y divide-gray-100">
          {GATEWAY_SERVICES.map((service) => {
            const serviceTasks = deploymentTasks?.data.filter((item) => item.adapter_type === service.adapterType) ?? []
            if (service.adapterType === 'terminal_dejavoo') {
              const addable = canManageGatewayServices && service.provisionable
              const terminalTasks = [...serviceTasks].sort(compareTerminalTasksByTpn)

              return (
                <div key={service.adapterType} className="py-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900">{service.name}</p>
                        {serviceTasks.length === 0 && (
                          <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${deploymentStatusClass('not_provisioned')}`}>
                            Not Provisioned
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-gray-500">{service.description}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {addable && (
                        <Button
                          variant="secondary"
                          onClick={() => onAddService(service)}
                        >
                          {serviceTasks.length > 0 ? 'Add another terminal' : 'Add Terminal'}
                        </Button>
                      )}
                    </div>
                  </div>

                  {terminalTasks.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {terminalTasks.map((terminalTask) => {
                        const failed = terminalTask.status === 'failed'
                        const cancellable = ['queued', 'failed', 'cancelled'].includes(terminalTask.status) && terminalTask.status !== 'cancelled'
                        const tpn = terminalTask.vendor_ref_id ?? ''
                        const terminalNumber = terminalNumberFromTpn(tpn)
                        const equipmentLabel = dejavooEquipmentLabel(terminalTask)
                        const statusState = tpn ? dejavooStatuses[tpn] : undefined
                        const activeSelection = tpn ? (dejavooActiveSelections[tpn] ?? 'active') : 'active'
                        const activeMessage = tpn ? dejavooActiveMessages[tpn] : null

                        return (
                          <div key={terminalTask.id} className="rounded-md border border-gray-200 px-3 py-2">
                            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    {terminalNumber === null ? 'Terminal' : `Terminal ${terminalNumber}`}
                                  </p>
                                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${deploymentStatusClass(terminalTask.status)}`}>
                                    {gatewayStatusLabel(terminalTask, service)}
                                  </span>
                                </div>
                                {equipmentLabel && (
                                  <p className="mt-1 text-xs font-medium text-gray-700">{equipmentLabel}</p>
                                )}
                                <p className="mt-1 text-xs text-gray-500">
                                  TPN: <span className="font-mono text-gray-700">{terminalTask.vendor_ref_id ?? '—'}</span>
                                  {' · '}
                                  Provisioned: {terminalTask.completed_at ? new Date(terminalTask.completed_at).toLocaleString() : '—'}
                                </p>
                                {statusState?.data && (
                                  <DejavooDownloadStatusDetails status={statusState.data} />
                                )}
                                {statusState?.error && (
                                  <p className="mt-1 text-xs text-rose-700">{statusState.error}</p>
                                )}
                                {activeMessage && (
                                  <p className={`mt-1 text-xs ${activeMessage.startsWith('Unable') ? 'text-rose-700' : 'text-emerald-700'}`}>
                                    {activeMessage}
                                  </p>
                                )}
                                {failed && terminalTask.last_error && (
                                  <p className="mt-1 text-xs text-rose-700" title={terminalTask.last_error}>
                                    {truncate(terminalTask.last_error)}
                                  </p>
                                )}
                              </div>

                              <div className="flex shrink-0 items-center gap-2">
                                {tpn && terminalTask.status === 'completed' && (
                                  <Button
                                    variant="secondary"
                                    onClick={() => void checkDejavooStatus(tpn)}
                                    disabled={statusState?.loading}
                                  >
                                    {statusState?.loading ? 'Checking...' : 'Check status'}
                                  </Button>
                                )}
                                {tpn && terminalTask.status === 'completed' && canManageGatewayServices && (
                                  <div className="flex items-center gap-2">
                                    <select
                                      className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                      value={activeSelection}
                                      onChange={(event) => {
                                        setDejavooActiveSelections((current) => ({
                                          ...current,
                                          [tpn]: event.target.value as DejavooActiveSelection,
                                        }))
                                      }}
                                    >
                                      <option value="active">Active</option>
                                      <option value="inactive">Inactive</option>
                                    </select>
                                    <Button
                                      variant="secondary"
                                      onClick={() => void submitDejavooActiveStatus(tpn)}
                                      disabled={dejavooActivePendingTpn === tpn}
                                    >
                                      {dejavooActivePendingTpn === tpn ? 'Saving...' : 'Set'}
                                    </Button>
                                  </div>
                                )}
                                {failed && canRetryDeploymentTasks && (
                                  <Button
                                    variant="secondary"
                                    onClick={() => onRetry(terminalTask.id)}
                                    disabled={retryPending}
                                  >
                                    {retryPending ? 'Retrying...' : 'Retry'}
                                  </Button>
                                )}
                                {cancellable && canManageGatewayServices && (
                                  <Button
                                    variant="secondary"
                                    onClick={() => onCancel(terminalTask)}
                                    disabled={cancelPending}
                                  >
                                    {cancelPending ? 'Cancelling...' : 'Cancel'}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            }

            const task = serviceTasks[0]
            const statusKey = service.provisionable ? task?.status ?? 'not_provisioned' : 'coming_soon'
            const failed = task?.status === 'failed'
            const cancellable = task && ['queued', 'failed', 'cancelled'].includes(task.status) && task.status !== 'cancelled'
            const completed = task?.status === 'completed'
            const addable = canManageGatewayServices && service.provisionable && (!task || task.status === 'cancelled')
            const showNmiStatus = service.adapterType === 'service_nmi' && completed
            const showAuthorizeNetStatus = service.adapterType === 'service_authorize_net' && !!task?.authorize_net_status
            const authorizeNetGatewayId = service.adapterType === 'service_authorize_net'
              ? task?.authorize_net_merchant_id ?? task?.vendor_ref_id
              : task?.vendor_ref_id
            const showAuthorizeNetActivationLink = service.adapterType === 'service_authorize_net'
              && !!task?.authorize_net_activation_link
              && task.authorize_net_status !== 'active'
            const headerBadge = badgeForService({
              service,
              task,
              statusKey,
              nmiStatus,
              nmiLoading: nmiStatusQuery.isFetching && !nmiStatus,
              showNmiStatus,
              showAuthorizeNetStatus,
            })

            return (
              <div key={service.adapterType} className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-gray-900">{service.name}</p>
                    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${headerBadge.className}`}>
                      {headerBadge.label}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">{service.description}</p>
                  {completed && (
                    <p className="mt-1 text-xs text-gray-500">
                      Gateway ID: <span className="font-mono text-gray-700">{authorizeNetGatewayId ?? '—'}</span>
                      {' · '}
                      Provisioned: {task.completed_at ? new Date(task.completed_at).toLocaleString() : '—'}
                    </p>
                  )}
                  {showNmiStatus && nmiStatusError && (
                    <p className="mt-2 text-xs text-rose-700">{nmiStatusError}</p>
                  )}
                  {failed && task.last_error && (
                    <p className="mt-1 text-xs text-rose-700" title={task.last_error}>
                      {truncate(task.last_error)}
                    </p>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  {showNmiStatus && canManageNmiStatus && (
                    <div className="flex items-center gap-2">
                      <select
                        className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        value={selectedNmiStatus}
                        onChange={(event) => setSelectedNmiStatus(event.target.value as NmiMutableStatus)}
                      >
                        {NMI_MUTABLE_STATUSES.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <Button
                        variant="secondary"
                        onClick={submitNmiStatus}
                        disabled={updateNmiStatusMutation.isPending || selectedNmiStatus === nmiStatus}
                      >
                        {updateNmiStatusMutation.isPending ? 'Saving...' : 'Set'}
                      </Button>
                    </div>
                  )}
                  {showAuthorizeNetActivationLink && (
                    <Button
                      variant="secondary"
                      onClick={() => {
                        void navigator.clipboard.writeText(task.authorize_net_activation_link ?? '')
                      }}
                    >
                      Copy activation link
                    </Button>
                  )}
                  {addable && (
                    <Button
                      variant="secondary"
                      onClick={() => onAddService(service)}
                    >
                      Add Service
                    </Button>
                  )}
                  {failed && canRetryDeploymentTasks && (
                    <Button
                      variant="secondary"
                      onClick={() => onRetry(task.id)}
                      disabled={retryPending}
                    >
                      {retryPending ? 'Retrying...' : 'Retry'}
                    </Button>
                  )}
                  {cancellable && canManageGatewayServices && (
                    <Button
                      variant="secondary"
                      onClick={() => onCancel(task)}
                      disabled={cancelPending}
                    >
                      {cancelPending ? 'Cancelling...' : 'Cancel'}
                    </Button>
                  )}
                  {!service.provisionable && (
                    <Button variant="secondary" disabled>
                      Coming Soon
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

function isMutableNmiStatus(status: string | null): status is NmiMutableStatus {
  return status === 'active' || status === 'closed' || status === 'restricted'
}

function badgeForService({
  service,
  task,
  statusKey,
  nmiStatus,
  nmiLoading,
  showNmiStatus,
  showAuthorizeNetStatus,
}: {
  service: GatewayServiceDefinition
  task: DeploymentTask | undefined
  statusKey: DeploymentTask['status'] | 'not_provisioned' | 'coming_soon'
  nmiStatus: string | null
  nmiLoading: boolean
  showNmiStatus: boolean
  showAuthorizeNetStatus: boolean
}): { label: string; className: string } {
  if (showAuthorizeNetStatus && task) {
    return {
      label: task.authorize_net_status_label ?? gatewayStatusLabel(task, service),
      className: authorizeNetStatusClass(task.authorize_net_status ?? null),
    }
  }

  if (showNmiStatus) {
    if (nmiLoading) {
      return {
        label: 'Loading...',
        className: nmiStatusClass(null),
      }
    }

    if (isMutableNmiStatus(nmiStatus)) {
      return {
        label: statusLabel(nmiStatus),
        className: nmiStatusClass(nmiStatus),
      }
    }
  }

  return {
    label: gatewayStatusLabel(task, service),
    className: deploymentStatusClass(statusKey),
  }
}

function authorizeNetStatusClass(status: string | null): string {
  switch (status) {
    case 'created_pending_activation':
    case 'submitted':
      return 'border-amber-200 bg-amber-50 text-amber-700'
    case 'active':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700'
    case 'rejected':
    case 'incomplete':
      return 'border-rose-200 bg-rose-50 text-rose-700'
    default:
      return deploymentStatusClass('completed')
  }
}

function statusLabel(status: string | null): string {
  if (!status) return 'Unknown'

  return NMI_MUTABLE_STATUSES.find((option) => option.value === status)?.label ?? status
}

function nmiStatusClass(status: string | null): string {
  switch (status) {
    case 'active':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700'
    case 'restricted':
      return 'border-amber-200 bg-amber-50 text-amber-700'
    case 'closed':
      return 'border-gray-300 bg-gray-50 text-gray-700'
    default:
      return 'border-slate-200 bg-slate-50 text-slate-600'
  }
}

function compareTerminalTasksByTpn(a: DeploymentTask, b: DeploymentTask): number {
  const aTpn = a.vendor_ref_id ?? ''
  const bTpn = b.vendor_ref_id ?? ''

  if (aTpn && bTpn) return aTpn.localeCompare(bTpn, undefined, { numeric: true })
  if (aTpn) return -1
  if (bTpn) return 1

  return (a.queued_at ?? '').localeCompare(b.queued_at ?? '')
}

function terminalNumberFromTpn(tpn: string | null): number | null {
  if (!tpn || tpn.length < 2) return null

  const suffix = tpn.slice(-2)
  if (!/^\d{2}$/.test(suffix)) return null

  return Number.parseInt(suffix, 10)
}

function dejavooEquipmentLabel(task: DeploymentTask): string | null {
  const data = task.result_data
  const profile = isRecord(data?.dejavoo_profile) ? data.dejavoo_profile : {}
  const pieces = [
    formatDejavooValue(getRecordString(data, 'device') ?? getRecordString(profile, 'device'), {
      qd2: 'QD2',
      qd4: 'QD4',
    }),
    formatDejavooValue(getRecordString(data, 'processor') ?? getRecordString(profile, 'processor'), {
      omaha: 'Omaha',
      nashville: 'Nashville',
    }),
    formatDejavooValue(getRecordString(data, 'business_type') ?? getRecordString(profile, 'business_type'), {
      retail: 'Retail',
      restaurant: 'Restaurant',
    }),
    formatDejavooValue(getRecordString(data, 'pricing') ?? getRecordString(profile, 'pricing'), {
      interchange: 'Interchange',
      flat_rate: 'Flat Rate',
    }),
  ].filter((piece): piece is string => !!piece)

  return pieces.length > 0 ? `Dejavoo ${pieces.join(' · ')}` : null
}

function formatDejavooValue(value: string | null, labels: Record<string, string>): string | null {
  if (!value) return null

  const normalized = value.trim().toLowerCase()
  return labels[normalized] ?? value.trim()
}

function getRecordString(record: unknown, key: string): string | null {
  if (!isRecord(record)) return null

  const value = record[key]
  return typeof value === 'string' && value.trim() !== '' ? value : null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function DejavooDownloadStatusDetails({ status }: { status: MerchantDejavooTerminalStatus }) {
  const details = [
    status.last_download ? `Last downloaded: ${formatDateTime(status.last_download)}` : null,
    status.last_loaded ? `Last loaded: ${formatDateTime(status.last_loaded)}` : null,
    status.serial_number ? `Device serial: ${status.serial_number}` : null,
    status.am_version_to ? `Version: ${status.am_version_to}` : null,
    status.network ? `Network: ${status.network}` : null,
  ].filter((item): item is string => !!item)

  return (
    <div className="mt-2 space-y-1">
      <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${dejavooDownloadStatusClass(status.status)}`}>
        {dejavooDownloadStatusLabel(status.status)}
      </span>
      {details.length > 0 && (
        <p className="text-xs text-gray-500">{details.join(' · ')}</p>
      )}
    </div>
  )
}

function dejavooDownloadStatusLabel(status: string | null): string {
  switch (status) {
    case 'loaded':
      return 'Loaded'
    case 'incomplete':
      return 'Incomplete'
    case 'no_download':
      return 'No download yet'
    default:
      return 'Unknown'
  }
}

function dejavooDownloadStatusClass(status: string | null): string {
  switch (status) {
    case 'loaded':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700'
    case 'incomplete':
      return 'border-amber-200 bg-amber-50 text-amber-700'
    case 'no_download':
      return 'border-slate-200 bg-slate-50 text-slate-600'
    default:
      return 'border-slate-200 bg-slate-50 text-slate-600'
  }
}

function formatDateTime(value: string): string {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}
